

import React, { useState, useRef, useEffect, ChangeEvent } from 'react';
import { apiService } from '../services/apiService';
import { ProfileData, ModalType, InterestsData, SavedCardType } from '../types';
import { SettingsIcon, ChevronDownIcon, UserIcon, CameraIcon, PencilIcon, MagicIcon, UploadSimpleIcon, TrashIcon } from './icons';
import { Avatar } from './Avatar';
import { SettingsDropdown } from './SettingsDropdown';
// R&D constants kept for backward-compat but section is hidden
import { COLLABORATION_TYPES, COLLABORATION_LEADS, COLLABORATION_TIMELINES, RD_BUDGETS } from '../constants';
import { InterestsPage } from './InterestsPage';
import { SavedPage } from './SavedPage';
import { OceanSavedTab } from './ocean/SavedView';
import { openInOcean } from './ocean/flags';
import { useFeatureAccess } from '../utils/featureAccess';
import BillingTab from './BillingTab';
import { trackClick } from '../utils/analytics';

/** Hidden, not deleted — same convention as SHOW_INBOX in SideNav.tsx.
 *  InterestsPage, the /interests API calls and the interestsData props all
 *  stay wired; flip this to true to bring the tab back. */
const SHOW_INTERESTS_TAB = false;
import ProfileFields, { scoreProfile } from './ProfileFields';
import { FloatingToast, InlineAlert } from './InlineAlert';
import { friendlyAuthError } from '../utils/authErrors';

// Legacy type kept locally so existing parent props still compile
interface RDProfileData {
  collaborationTypes: string[];
  collaborationLead: string[];
  collaborationTimeline: string;
  rdBudget: string;
  otherInfo: string;
  corporateDeckUrl: string;
  rdSummary: string;
  corporateDeckFile: string;
  companyLinkedinPage?: string;
}

const HATS_OPTIONS = [
  "Researcher", "Tech Transfer Office", "Student", "Faculty",
  "University Administrator", "Industry", "SME or Startup", "Investor",
  "Consultant", "Non Profit", "Government",
  "Accelerator, Incubator or Venture Studio",
];

const INTENT_OPTIONS = [
  "Uncover White Spaces", "Ideate & Brainstorm", "Shape Innovation Policy",
  "Benchmark & Landscape", "Out-Licence Tech", "Spinout", "In-Licence Tech",
  "Acquire Tech", "Co-Develop Tech", "Host Open Innovation Challenges",
  "Fund Spinout", "Fund POC or Pilot", "Sponsor Research",
  "Consult Technical Expert", "Access Labs", "Manage IP & Portfolio",
  "Help with Valuation, Legal or Contracts",
];

const PROFILE_WEIGHTS: Record<string, number> = {
  firstName: 10, lastName: 10, email: 10,
  contactNumber: 15, city: 5, country: 5,
  companyName: 5, designation: 5, companyWebsite: 5,
  qualification: 5, linkedin: 15,
  hats: 15, intent: 15,
};
const PROFILE_MAX = Object.values(PROFILE_WEIGHTS).reduce((a, b) => a + b, 0);

const FormInput = ({ label, value, onChange, name, placeholder, type = 'text', disabled = false, children }: { label: string; value: string; onChange: (e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void; name: keyof ProfileData | 'password'; placeholder?: string; type?: string; disabled?: boolean; children?: React.ReactNode }) => (
    <div className="flex flex-col items-start gap-1.5 flex-1 w-full">
        <label className="text-grayscale-1100 font-grotesk text-xs font-normal">{label}</label>
        <div className="flex h-11 py-3 px-2.5 items-center gap-2.5 self-stretch rounded-md border-primary-300 border-[0.5px] bg-white">
            <input
                type={type}
                placeholder={placeholder}
                name={name}
                value={value}
                onChange={onChange}
                disabled={disabled}
                className="flex-1 bg-transparent text-grayscale-1300 font-grotesk text-sm focus:outline-none"
            />
            {children}
        </div>
    </div>
);

const TagButton = ({ text, active, onClick }: { text: string; active: boolean; onClick: () => void; }) => (
    <button
        onClick={onClick}
        className={`py-1 px-4 rounded-md border text-xs font-grotesk transition-colors ${
            active
                ? 'bg-secondary-300/20 border-secondary-300 text-[#2C5695] font-medium'
                : 'bg-transparent border-grayscale-300 text-grayscale-900 hover:bg-grayscale-200'
        }`}
    >
        {text}
    </button>
);


interface ProfilePageProps {
  profileData: ProfileData;
  setProfileData: React.Dispatch<React.SetStateAction<ProfileData>>;
  initialProfileData: ProfileData;
  rdProfileData: RDProfileData;
  setRDProfileData: React.Dispatch<React.SetStateAction<RDProfileData>>;
  initialRDProfileData: RDProfileData;
  interestsData: InterestsData;
  setInterestsData: React.Dispatch<React.SetStateAction<InterestsData>>;
  initialInterestsData: InterestsData;
  openModal: (type: ModalType) => void;
  onReadMore: (type: SavedCardType, id: string) => void;
  onLogout?: () => void;
  refreshProfileData?: () => Promise<void>;
  userHasPassword?: boolean;
  setActiveView?: (view: string) => void;
  initialTab?: string;
  /** Told whenever the open tab changes (the app's mobile header names it). */
  onTabChange?: (tab: string) => void;
}

const useAutosizeTextArea = (textAreaRef: React.RefObject<HTMLTextAreaElement>, value: string) => {
    useEffect(() => {
        if (textAreaRef.current) {
            const style = window.getComputedStyle(textAreaRef.current);
            let lineHeight = parseInt(style.lineHeight, 10);
            if (isNaN(lineHeight)) {
                const fontSize = parseInt(style.fontSize, 10);
                lineHeight = Math.floor(fontSize * 1.4); // A common approximation for 'normal' line-height
            }

            textAreaRef.current.style.height = "auto";
            const scrollHeight = textAreaRef.current.scrollHeight;
            const maxLines = 10;
            const maxHeight = lineHeight * maxLines;

            if (maxHeight > 0 && scrollHeight > maxHeight) {
                textAreaRef.current.style.height = `${maxHeight}px`;
                textAreaRef.current.style.overflowY = 'scroll';
            } else {
                textAreaRef.current.style.height = `${scrollHeight}px`;
                textAreaRef.current.style.overflowY = 'hidden';
            }
        }
    }, [textAreaRef, value]);
};

export const ProfilePage: React.FC<ProfilePageProps> = ({ 
    profileData, setProfileData, initialProfileData, 
    rdProfileData, setRDProfileData, initialRDProfileData, 
    interestsData, setInterestsData, initialInterestsData,
    openModal, onReadMore, onLogout, refreshProfileData, setActiveView,
    initialTab = 'profile', userHasPassword = true, onTabChange
}) => {
    const [localProfileData, setLocalProfileData] = useState<ProfileData>(profileData);
    const [localRDProfileData, setLocalRDProfileData] = useState<RDProfileData>(rdProfileData);
    const [isDirty, setIsDirty] = useState(false);
    const [profileType, setProfileType] = useState('personal');
    const [profileImage, setProfileImage] = useState<string | null>(null);
    const [fixingSummary, setFixingSummary] = useState(false);
    const [fixingRDSummary, setFixingRDSummary] = useState(false);
    // Completion now comes from the server's spec (weights total 100), not a
    // second weight table that summed to 120 and was clamped at 100.
    const [completionPercentage, setCompletionPercentage] = useState(0);
    const [rdCompletionPercentage, setRDCompletionPercentage] = useState(0);
    // ForYouPage's pencil deep-links to 'interests'. With the tab hidden that
    // would render a page with nothing selected and no body, so fall back.
    // 'ocean' was a tab of its own; it is now Saved's OCEAN section, so old
    // links and state that name it land on Saved, on that section.
    const [activeTab, setActiveTab] = useState(
        initialTab === 'interests' && !SHOW_INTERESTS_TAB ? 'profile' : initialTab === 'ocean' ? 'saved' : initialTab);
    useEffect(() => { onTabChange?.(activeTab); }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps
    // Saved → OCEAN (saved OCEAN cards and collections) is there for accounts OCEAN lets in.
    const access = useFeatureAccess();
    const oceanTab = access.features.ocean.enabled;
    const [savedSection, setSavedSection] = useState<'tech' | 'ocean'>(initialTab === 'ocean' ? 'ocean' : 'tech');
    useEffect(() => {
        if (activeTab === 'ocean') { setActiveTab('saved'); setSavedSection('ocean'); }
    }, [activeTab]);
    // Values keyed by the SERVER's field keys (profile_spec.FIELD_SPEC).
    const [specValues, setSpecValues] = useState<Record<string, any>>({});
    const [profileSpec, setProfileSpec] = useState<any>(null);
    const [specLoaded, setSpecLoaded] = useState(false);
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isPasswordChanging, setIsPasswordChanging] = useState(false);
    const [showPasswordChange, setShowPasswordChange] = useState(false);
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [saveMessage, setSaveMessage] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [successMessage, setSuccessMessage] = useState<string>('');
    const [errorMessage, setErrorMessage] = useState<string>('');
    const settingsRef = useRef<HTMLDivElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const rdFileInputRef = useRef<HTMLInputElement>(null);
    const rdSummaryTextareaRef = useRef<HTMLTextAreaElement>(null);
    
    useAutosizeTextArea(rdSummaryTextareaRef, localRDProfileData.rdSummary);

    useEffect(() => {
        setLocalProfileData({
            ...profileData,
            hats: profileData.hats ?? [],
            intent: profileData.intent ?? [],
            companyWebsite: profileData.companyWebsite ?? '',
        });
        setLocalRDProfileData(rdProfileData);
    }, [profileData, rdProfileData]);

    // Load profile image from props
    useEffect(() => {
        if (profileData.profileImage) {
            setProfileImage(profileData.profileImage);
        }
    }, [profileData.profileImage]);

    useEffect(() => {
        const isProfileDirty = JSON.stringify(localProfileData) !== JSON.stringify(profileData);
        const isRDProfileDirty = JSON.stringify(localRDProfileData) !== JSON.stringify(rdProfileData);
        // The password is not part of the profile form any more — it is changed
        // through its own dialog, which saves on its own. Treating it as unsaved
        // profile state made the page look dirty when nothing was pending.
        setIsDirty(isProfileDirty || isRDProfileDirty);
    }, [localProfileData, profileData, localRDProfileData, rdProfileData]);

    // Autosave: debounce 5 seconds after a user-driven change, stop after saving
    const autosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const isSavingRef = useRef(false);
    const justSavedRef = useRef(false);
    const [lastAutoSaved, setLastAutoSaved] = useState('');

    useEffect(() => {
        // Skip if we're mid-save or if the change came from a post-save refresh
        if (isSavingRef.current) return;
        if (justSavedRef.current) {
            justSavedRef.current = false;
            return;
        }

        const isProfileDirty = JSON.stringify(localProfileData) !== JSON.stringify(profileData);
        const isRDProfileDirty = JSON.stringify(localRDProfileData) !== JSON.stringify(rdProfileData);
        if (!isProfileDirty && !isRDProfileDirty) return;

        if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
        autosaveTimerRef.current = setTimeout(() => {
            if (isSavingRef.current) return;
            isSavingRef.current = true;
            justSavedRef.current = true;
            handleSaveChanges().then(() => {
                const now = new Date();
                setLastAutoSaved(`Auto-saved at ${now.toLocaleTimeString()}`);
                setTimeout(() => setLastAutoSaved(''), 3000);
            }).finally(() => {
                isSavingRef.current = false;
            });
        }, 5000);

        return () => {
            if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [localProfileData, localRDProfileData]);

    useEffect(() => {
        // Scored from the SERVER's spec so this bar, /auth/status and the
        // admin panel all report the same completion for the same profile.
        if (!profileSpec) return;
        setCompletionPercentage(scoreProfile(profileSpec, specValues).percentage);
    }, [profileSpec, specValues]);
    
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (settingsRef.current && !settingsRef.current.contains(event.target as Node)) {
                setIsSettingsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [settingsRef]);

    const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        
        // Apply 500 character limit for summary field when user is typing
        if (name === 'summary' && value.length > 500) {
            return; // Don't allow user to type more than 500 characters
        }
        
        setLocalProfileData(prev => ({ ...prev, [name]: value }));
    };

    const handleRDChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        
        // Apply 500 character limit for rdSummary field when user is typing
        if (name === 'rdSummary' && value.length > 500) {
            return; // Don't allow user to type more than 500 characters
        }
        
        setLocalRDProfileData(prev => ({ ...prev, [name]: value }));
    };

    const handleTagToggle = (field: 'collaborationTypes' | 'collaborationLead', value: string) => {
        setLocalRDProfileData(prev => {
            const currentValues = prev[field];
            const newValues = currentValues.includes(value)
                ? currentValues.filter(v => v !== value)
                : [...currentValues, value];
            return { ...prev, [field]: newValues };
        });
    }
    
    // Hydrate the spec form from the API's own field names. The legacy
    // ProfileData object is camelCase and lossy (no title/personas/goals), so
    // the new fields are read straight from GET /api/v1/me.
    useEffect(() => {
        const userId = apiService.getStoredUserId();
        if (!userId || specLoaded) return;
        (async () => {
            const res = await apiService.getProfile(userId);
            // A failed fetch must NOT hydrate: setting specValues from {}
            // and then saving would write empty strings over a real profile.
            if (!res.success || !res.data) {
                setErrorMessage('Could not load your profile — refresh before editing.');
                return;
            }
            const p: any = (res.data as any).profile || {};
            const u: any = (res.data as any).user || {};
            setSpecValues({
                email: u.email || localProfileData.email,
                title: p.title || '',
                first_name: u.first_name ?? localProfileData.firstName,
                last_name: u.last_name ?? localProfileData.lastName,
                phone: p.phone || localProfileData.contactNumber,
                city: u.city ?? localProfileData.city,
                country: u.country ?? localProfileData.country,
                industries: p.industries || [],
                interests: (res.success && (res.data as any).interests) || interestsData?.keywords || [],
                organisation_name: p.organisation_name || localProfileData.companyName,
                designation: p.designation || localProfileData.designation,
                company_website_url: p.company_website_url || localProfileData.companyWebsite,
                linkedin_profile_url: p.linkedin_profile_url || localProfileData.linkedin,
                personas: p.personas || [],
                rd_goals: p.rd_goals || [],
                rd_goal_details: p.rd_goal_details || [],
                rd_problem_statement: p.rd_problem_statement || '',
                additional_problem_statements: p.additional_problem_statements || [],
                priority_tags: p.priority_tags || [],
            });
            setSpecLoaded(true);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [specLoaded]);

    const handleSpecChange = (key: string, value: any) => {
        setSpecValues(prev => {
            const next: Record<string, any> = { ...prev, [key]: value };
            const groups = (profileSpec?.options?.persona_goals || {}) as
                Record<string, { label: string }[]>;

            // Picking a goal no longer selects a persona. It used to map the
            // goal's LABEL back to every persona owning it, and Industry and
            // Consultant are offered the identical five goals — so answering
            // anything under Industry silently ticked Consultant as well.
            // Personas are chosen by the person, and only by the person.

            // Switching persona does NOT delete the other's answers. The
            // chips are tabs now, and someone clicking through them to read
            // the questions must not lose what they already ticked. Values are
            // stored persona-qualified ("Industry::…"), so an answer always
            // says which hat it was given under and nothing is ambiguous —
            // only the selected persona's are shown.
            //
            // Legacy unqualified entries are the exception: they carry no
            // persona, so they are kept only while some persona still offers
            // that label, otherwise they would show up under whoever is
            // selected next.
            if (key === 'personas') {
                const kept: string[] = Array.isArray(value) ? value : [];
                const goals: string[] = Array.isArray(next.rd_goals) ? next.rd_goals : [];
                if (goals.length && kept.length) {
                    const offered = new Set(Object.keys(groups).flatMap(
                        pn => (groups[pn] || []).map(g => g.label)));
                    const trimmed = goals.filter(g =>
                        String(g).includes('::') || offered.has(String(g)));
                    if (trimmed.length !== goals.length) next.rd_goals = trimmed;
                }
            }
            return next;
        });
        setIsDirty(true);
    };

    const handleSaveSpec = async () => {
        const userId = apiService.getStoredUserId();
        if (!userId) return;
        setIsSaving(true);
        // Straight through: the spec keys ARE the API's field names, apart
        // from the two the write path aliases.
        const payload: Record<string, any> = { ...specValues };
        payload.company = specValues.organisation_name;
        payload.linkedin_url = specValues.linkedin_profile_url;
        if (profileImage) payload.profile_picture_url = profileImage;
        const res = await apiService.updateProfile(userId, payload);
        setIsSaving(false);
        if (res.success) {
            setIsDirty(false);
            setLastAutoSaved(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
            setSuccessMessage('Profile saved');
            setTimeout(() => setSuccessMessage(''), 4000);
            refreshProfileData?.();
        } else {
            setErrorMessage(res.error || 'Could not save your profile');
        }
    };

    const handleSaveChanges = async () => {
        setIsSaving(true);
        setSaveMessage('');
        
        try {
            const userId = apiService.getStoredUserId();
            if (!userId) {
                setSaveMessage('User session expired. Please log in again.');
                setIsSaving(false);
                return;
            }

            const profilePayload = {
                email: localProfileData.email,
                first_name: localProfileData.firstName,
                last_name: localProfileData.lastName,
                phone: localProfileData.contactNumber,
                city: localProfileData.city,
                country: localProfileData.country,
                designation: localProfileData.designation,
                company: localProfileData.companyName,
                company_website_url: localProfileData.companyWebsite,
                linkedin_url: localProfileData.linkedin,
                professional_summary: localProfileData.summary,
                highest_qualification: localProfileData.qualification,
                profile_picture_url: profileImage,
                hats: localProfileData.hats,
                intent: localProfileData.intent,
                // R&D profile data (kept for backward compatibility)
                collaboration_types: localRDProfileData.collaborationTypes,
                collaboration_lead: localRDProfileData.collaborationLead,
                user_roles: [],
                collaboration_timeline: localRDProfileData.collaborationTimeline,
                rd_budget: localRDProfileData.rdBudget,
                company_linkedin_page: localRDProfileData.companyLinkedinPage || '',
                corporate_deck_url: localRDProfileData.corporateDeckUrl || '',
                rd_summary: localRDProfileData.rdSummary,
                other_info: localRDProfileData.otherInfo
            };

            // Call update profile API
            const response = await apiService.updateProfile(userId, profilePayload);
            
            if (response.success) {
                // Check if auto-assigned interests were added
                if (response.data?.auto_assigned_interests) {
                    const autoAssigned = response.data.auto_assigned_interests;
                    console.log('🎯 Auto-assigned interests received:', autoAssigned);
                    
                    // Update interests data with the new auto-assigned keywords
                    if (autoAssigned.all_interests && Array.isArray(autoAssigned.all_interests)) {
                        setInterestsData(prev => ({
                            ...prev,
                            keywords: autoAssigned.all_interests
                        }));
                        
                        // Show success message about auto-assigned keywords
                        const assignedCount = autoAssigned.keywords_assigned?.length || 0;
                        if (assignedCount > 0) {
                            setSaveMessage(`Profile saved successfully! We've also added ${assignedCount} recommended interests based on your profile: ${autoAssigned.keywords_assigned.join(', ')}`);
                        } else {
                            setSaveMessage('Profile saved successfully!');
                        }
                    } else {
                        setSaveMessage('Profile saved successfully!');
                    }
                } else {
                    setSaveMessage('Profile saved successfully!');
                }
                
                // Update local state on success
                setProfileData(localProfileData);
                setRDProfileData(localRDProfileData);
                
                // Refresh profile data to get any auto-assigned interests
                if (refreshProfileData) {
                    try {
                        await refreshProfileData();
                        console.log('✅ Profile data refreshed after save');
                    } catch (error) {
                        console.error('Failed to refresh profile data:', error);
                    }
                }
                
                // Clear message after 3 seconds
                setTimeout(() => setSaveMessage(''), 3000);
            } else {
                setSaveMessage(friendlyAuthError(response.error, 'Failed to save profile. Please try again.'));
            }
        } catch (error) {
            console.error('Error saving profile:', error);
            setSaveMessage('Network error. Please try again.');
        } finally {
            setIsSaving(false);
        }
    };

    const handlePasswordSave = async () => {
        if (newPassword !== confirmPassword) {
            setPasswordError('New passwords do not match');
            return;
        }

        if (newPassword.length < 8) {
            setPasswordError('Password must be at least 8 characters long');
            return;
        }

        setIsPasswordChanging(true);
        setPasswordError('');

        try {
            const userId = apiService.getStoredUserId();
            if (!userId) {
                setPasswordError('User session expired. Please log in again.');
                setIsPasswordChanging(false);
                return;
            }

            // Session proves identity — no current password required
            const setResponse = await apiService.setPassword(userId, newPassword);
            if (setResponse.success) {
                setShowPasswordChange(false);
                setSaveMessage('Password updated successfully!');
                setTimeout(() => setSaveMessage(''), 3000);
                try {
                    await refreshProfileData?.();
                } catch {
                    /* non-fatal */
                }
            } else {
                setPasswordError(friendlyAuthError(setResponse.error, 'Failed to update password. Please try again.'));
            }
        } catch (error) {
            console.error('Error changing password:', error);
            setPasswordError('Network error. Please try again.');
        } finally {
            setIsPasswordChanging(false);
        }
    };

    const handlePasswordCancel = () => {
        setShowPasswordChange(false);
        setNewPassword('');
        setConfirmPassword('');
        setPasswordError('');
    };

    const handleImageUploadClick = () => {
        fileInputRef.current?.click();
    };

    const handleImageChange = (e: ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            // Check file size (limit to 5MB)
            if (file.size > 5 * 1024 * 1024) {
                alert('File size too large. Please choose an image smaller than 5MB.');
                return;
            }
            
            // Convert to base64 for preview and storage
            const reader = new FileReader();
            reader.onload = (event) => {
                const base64String = event.target?.result as string;
                setProfileImage(base64String);
                setIsDirty(true); // Mark as dirty so user knows to save
            };
            reader.readAsDataURL(file);
        }
    };

    const handleRDFileUploadClick = () => {
        rdFileInputRef.current?.click();
    };

    const [uploadedRDFile, setUploadedRDFile] = useState<File | null>(null);
    const [savedRDFile, setSavedRDFile] = useState<{id: string, name: string, processing_info?: any} | null>(null);
    const [isUploadingFile, setIsUploadingFile] = useState(false);

    // Load saved R&D file on component mount
    useEffect(() => {
        const loadSavedRDFile = async () => {
            const userId = apiService.getStoredUserId();
            if (userId) {
                try {
                    const response = await apiService.getUserFiles(userId, 'rd_profile_deck');
                    if (response.success && response.data?.files.length > 0) {
                        const latestFile = response.data.files[0]; // Most recent file
                        setSavedRDFile({
                            id: latestFile.id,
                            name: latestFile.file_name,
                            processing_info: latestFile.processing_metadata
                        });
                        setLocalRDProfileData(prev => ({ ...prev, corporateDeckFile: latestFile.file_name }));
                    }
                } catch (error) {
                    console.error('Error loading saved R&D file:', error);
                }
            }
        };
        loadSavedRDFile();
    }, []);

    const handleRDFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) { // 5MB limit
                alert('File size must be under 5MB');
                return;
            }
            if (file.type !== 'application/pdf') {
                alert('Please upload a PDF file');
                return;
            }
            
            setIsUploadingFile(true);
            try {
                const userId = apiService.getStoredUserId();
                if (userId) {
                    // Delete existing file if any
                    if (savedRDFile) {
                        await apiService.deleteUserFile(savedRDFile.id, userId);
                    }
                    
                    // Upload new file
                    const uploadResponse = await apiService.uploadFile(userId, file, 'rd_profile_deck');
                    if (uploadResponse.success && uploadResponse.data) {
                        setSavedRDFile({
                            id: uploadResponse.data.file_id,
                            name: uploadResponse.data.file_name,
                            processing_info: uploadResponse.data.processing_info
                        });
                        setUploadedRDFile(file);
                        setLocalRDProfileData(prev => ({ ...prev, corporateDeckFile: file.name }));
                        console.log(`📎 Uploaded and processed ${uploadResponse.data.processing_info?.pages || 1} pages`);
                        setSuccessMessage(`File uploaded successfully! Processed ${uploadResponse.data.processing_info?.pages || 1} pages`);
                        setTimeout(() => setSuccessMessage(''), 5000);
                    } else {
                        setErrorMessage(friendlyAuthError(uploadResponse.error, 'Failed to upload file. Please try again.'));
                        setTimeout(() => setErrorMessage(''), 5000);
                    }
                }
            } catch (error) {
                console.error('Error uploading R&D file:', error);
                setErrorMessage('Error uploading file. Please try again.');
                setTimeout(() => setErrorMessage(''), 5000);
            } finally {
                setIsUploadingFile(false);
            }
        }
    };

    const handleDeleteRDFile = async () => {
        if (savedRDFile) {
            try {
                const userId = apiService.getStoredUserId();
                if (userId) {
                    const response = await apiService.deleteUserFile(savedRDFile.id, userId);
                    if (response.success) {
                        setSavedRDFile(null);
                        setUploadedRDFile(null);
                        setLocalRDProfileData(prev => ({ ...prev, corporateDeckFile: '' }));
                        console.log('📎 R&D file deleted successfully');
                    } else {
                        alert('Failed to delete file');
                    }
                }
            } catch (error) {
                console.error('Error deleting R&D file:', error);
                alert('Error deleting file. Please try again.');
            }
        }
    };
    
    const handleFixSummary = async () => {
        setFixingSummary(true);
        try {
            const userId = apiService.getStoredUserId();
            if (!userId) {
                console.error("User not logged in");
                alert("Please log in to use the Fix Text feature.");
                return;
            }
            
            // Use enhanced API with LinkedIn context
            const response = await apiService.enhanceText(
                localProfileData.summary,
                'professional_summary',
                userId,
                localProfileData.linkedin || undefined,
                localRDProfileData.companyLinkedinPage || undefined
            );
            
            if (response.success && response.data) {
                setLocalProfileData(prev => ({ ...prev, summary: response.data.enhanced_text }));
            } else {
                console.error("Failed to enhance text:", response.error);
                alert(friendlyAuthError(response.error, 'Failed to enhance text. Please try again.'));
            }
        } catch (error) {
            console.error("Error fixing summary:", error);
            alert("Network error occurred. Please check your connection and try again.");
        } finally {
            setFixingSummary(false);
        }
    };

    const handleFixRDSummary = async () => {
        setFixingRDSummary(true);
        try {
            const userId = apiService.getStoredUserId();
            if (!userId) {
                console.error("User not logged in");
                alert("Please log in to use the Fix Text feature.");
                return;
            }
            
            // Use file-enhanced API if there's a saved file, otherwise use regular API
            const response = (savedRDFile && uploadedRDFile)
                ? await apiService.enhanceTextWithFile(
                    localRDProfileData.rdSummary,
                    'rd_summary',
                    userId,
                    uploadedRDFile,
                    localProfileData.linkedin || undefined,
                    localRDProfileData.companyLinkedinPage || undefined
                  )
                : await apiService.enhanceText(
                    localRDProfileData.rdSummary,
                    'rd_summary',
                    userId,
                    localProfileData.linkedin || undefined,
                    localRDProfileData.companyLinkedinPage || undefined
                  );
            
            if (response.success && response.data) {
                setLocalRDProfileData(prev => ({ ...prev, rdSummary: response.data.enhanced_text }));
                
                // Show additional info if file was processed
                if ((response.data as any).file_processed && (response.data as any).images_processed) {
                    console.log(`📎 Processed ${(response.data as any).images_processed} images from uploaded deck`);
                }
            } else {
                console.error("Failed to enhance R&D summary:", response.error);
                alert(friendlyAuthError(response.error, 'Failed to enhance R&D summary. Please try again.'));
            }
        } catch (error) {
            console.error("Error fixing R&D summary:", error);
            alert("Network error occurred. Please check your connection and try again.");
        } finally {
            setFixingRDSummary(false);
        }
    };
    
    const TabButton = ({ label, id }: { label: string, id: string }) => (
        <button
            onClick={() => { trackClick(`profile_click_tab_${id}`, { tab: id }); setActiveTab(id); }}
            className={`flex h-10 py-2 px-4 justify-center items-center gap-2.5 ${
                activeTab === id ? 'border-b-2 border-primary-700' : ''
            }`}
        >
            <span className={`font-grotesk text-sm whitespace-nowrap ${
                activeTab === id ? 'text-primary-700 font-bold' : 'text-grayscale-1200'
            }`}>
                {label}
            </span>
        </button>
    );

    return (
        <main className="flex flex-col flex-1 self-stretch rounded-2xl bg-grayscale-100 shadow-custom-light p-4 gap-5 overflow-y-auto hide-scrollbar overflow-x-hidden">
            {/* Header */}
            <div className="flex items-start gap-4 self-stretch border-b border-grayscale-800">
                <div className="flex flex-1 items-center overflow-x-auto hide-scrollbar">
                    <TabButton label="Profile" id="profile" />
                    {SHOW_INTERESTS_TAB && <TabButton label="Interests" id="interests" />}
                    <TabButton label="Saved" id="saved" />
                    <TabButton label="Billing" id="billing" />
                </div>
                <div className="relative" ref={settingsRef}>
                    <button onClick={() => setIsSettingsOpen(prev => !prev)} className="flex h-12 p-3 justify-center items-center gap-1 rounded-xl">
                        <SettingsIcon />
                        <ChevronDownIcon className="w-3.5 h-3.5" />
                    </button>
                    {isSettingsOpen && <SettingsDropdown openModal={openModal} onLogout={onLogout} setActiveView={setActiveView} />}
                </div>
            </div>

            {/* Sub-header */}
            {activeTab === 'profile' && (
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center self-stretch gap-4">
                    {/* The number was previously hidden on the reasoning that a
                        percentage invites gaming rather than answering. Asked for
                        back (2026-09-10): people could see a bar moving and had no
                        way to tell how much was left. The weights behind it stay
                        hidden, so it says how far along you are without saying
                        which answer would move it most. */}
                    <div className="flex items-center gap-3">
                        <span className="text-sm font-grotesk font-medium text-[#2C5695]">
                            {completionPercentage >= 100 ? 'Profile complete' : 'Complete your profile'}
                        </span>
                        <div className="w-32 h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div className="h-full bg-[#2C5695] rounded-full transition-all" style={{ width: `${Math.min(completionPercentage, 100)}%` }} />
                        </div>
                        <span className="text-sm font-grotesk font-semibold text-[#2C5695] tabular-nums"
                              title="How much of your profile is filled in">
                            {Math.round(Math.min(completionPercentage, 100))}%
                        </span>
                    </div>
                    <div className="flex items-center gap-3">
                        {lastAutoSaved && (
                            <span className="text-xs font-grotesk text-green-600 italic">{lastAutoSaved}</span>
                        )}
                        <button 
                            onClick={handleSaveChanges}
                            className={`hidden sm:block py-2.5 px-4 rounded-full font-grotesk text-sm font-medium ${isDirty && !isSaving ? 'bg-primary-500 text-grayscale-100' : 'bg-primary-100 text-primary-300'}`}
                            disabled={!isDirty || isSaving}
                        >
                            {isSaving ? 'Saving...' : 'Save Changes'}
                        </button>
                    </div>
                </div>
            )}
            
            {activeTab === 'profile' && (
                <>
                {/* Photo stays hand-rolled: it is an upload widget, not a
                    scored field, so it is deliberately outside the spec. */}
                <div className="flex items-center gap-4">
                    <div className="relative w-[88px] h-[88px] flex-shrink-0">
                        <div className="w-full h-full rounded-full border-[0.5px] border-primary-400 overflow-hidden relative">
                            <Avatar
                                firstName={specValues.first_name || localProfileData.firstName}
                                lastName={specValues.last_name || localProfileData.lastName}
                                email={specValues.email || localProfileData.email}
                                profileImage={profileImage}
                                size="lg"
                                className="border-0"
                            />
                        </div>
                        <input type="file" ref={fileInputRef} onChange={handleImageChange} accept="image/*" className="hidden" />
                        <button onClick={handleImageUploadClick} className="absolute bottom-0 right-0 w-8 h-8 rounded-full bg-primary-500 flex items-center justify-center shadow-custom-light p-1">
                            <CameraIcon />
                        </button>
                    </div>
                    <div className="flex flex-col gap-1">
                        <span className="text-grayscale-1100 font-grotesk text-xs">Password</span>
                        {/* Two different operations, deliberately routed apart.
                            Changing an existing password goes through the dialog
                            that verifies the current one server-side. Setting a
                            first password (a Google/LinkedIn signup) has nothing
                            to verify against, so it keeps the inline form. */}
                        {userHasPassword ? (
                            <button onClick={() => openModal(ModalType.ChangePassword)}
                                className="text-sm text-primary-700 underline self-start">Change password</button>
                        ) : (
                            <button onClick={() => setShowPasswordChange(true)}
                                className="text-sm text-primary-700 underline self-start">Set a password</button>
                        )}
                    </div>
                </div>

                <ProfileFields
                    values={specValues}
                    onChange={handleSpecChange}
                    onSpecLoaded={setProfileSpec}
                />

                <div className="flex items-center gap-3 pt-2">
                    <button
                        onClick={handleSaveSpec}
                        disabled={!isDirty || isSaving}
                        className="py-2.5 px-4 rounded-full font-grotesk text-sm font-medium bg-primary-500 text-grayscale-100 disabled:bg-primary-100 disabled:text-primary-300">
                        {isSaving ? 'Saving…' : 'Save changes'}
                    </button>
                    {lastAutoSaved && <span className="text-xs text-grayscale-1100">Saved at {lastAutoSaved}</span>}
                </div>
                </>
            )}

            {SHOW_INTERESTS_TAB && activeTab === 'interests' && (
                <InterestsPage 
                    interestsData={interestsData}
                    setInterestsData={setInterestsData}
                />
            )}
            {activeTab === 'billing' && (
                <BillingTab />
            )}
            {activeTab === 'saved' && oceanTab && (
                <div className="flex items-center gap-1 p-1 rounded-full bg-grayscale-200 w-fit" role="group" aria-label="Saved section">
                    {([['tech', 'Technologies'], ['ocean', 'OCEAN']] as const).map(([id, label]) => (
                        <button key={id} type="button" aria-pressed={savedSection === id}
                            onClick={() => { trackClick(`profile_click_saved_${id}`, { section: id }); setSavedSection(id); }}
                            className={`py-1.5 px-4 rounded-full font-grotesk text-sm ${
                                savedSection === id ? 'bg-grayscale-100 text-primary-700 font-bold shadow-custom-light' : 'text-grayscale-1100'
                            }`}>
                            {label}
                        </button>
                    ))}
                </div>
            )}
            {activeTab === 'saved' && oceanTab && savedSection === 'ocean' && (
                <OceanSavedTab
                    onOpen={(kind, id) => { openInOcean(kind, id); setActiveView?.('ocean'); }}
                    onOpenTech={id => onReadMore(SavedCardType.Tech, id)}
                />
            )}
            {activeTab === 'saved' && (!oceanTab || savedSection === 'tech') && (
                <SavedPage 
                    onTalkToConcierge={() => openModal(ModalType.BookCall)}
                    onOpenTech={id => onReadMore(SavedCardType.Tech, id)}
                    profileData={{
                        firstName: localProfileData.firstName,
                        lastName: localProfileData.lastName,
                        summary: localProfileData.summary
                    }}
                    rdProfileData={{
                        rdSummary: localRDProfileData.rdSummary
                    }}
                />
            )}

            {/* Save Message */}
            {saveMessage && (
                <FloatingToast
                    variant={saveMessage.includes('successfully') ? 'success' : 'error'}
                    message={saveMessage}
                />
            )}

            {/* Password Change Modal */}
            {showPasswordChange && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6">
                        <h3 className="text-lg font-grotesk font-medium mb-4">Set a password</h3>
                        <p className="text-sm text-gray-600 mb-4">
                            Your account was created with Google or LinkedIn, so there is no
                            current password to confirm. Setting one lets you sign in with
                            your email as well.
                        </p>
                        
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                                <input
                                    type="password"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                                    placeholder="Enter new password"
                                />
                            </div>
                            
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
                                <input
                                    type="password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                                    placeholder="Confirm new password"
                                />
                            </div>
                            
                            {passwordError && (
                                <InlineAlert variant="error" compact>{passwordError}</InlineAlert>
                            )}
                        </div>
                        
                        <div className="flex justify-end gap-3 mt-6">
                            <button
                                onClick={handlePasswordCancel}
                                disabled={isPasswordChanging}
                                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handlePasswordSave}
                                disabled={isPasswordChanging || !newPassword || !confirmPassword}
                                className="px-4 py-2 bg-primary-500 text-white rounded-md hover:bg-primary-600 disabled:opacity-50"
                            >
                                {isPasswordChanging ? 'Saving...' : 'Save Password'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Success/Error Notifications */}
            {successMessage && (
                <div className="fixed inset-x-0 top-0 z-50 flex justify-center pt-4 sm:pt-6 pointer-events-none">
                    <div className="pointer-events-auto p-4 bg-green-100 border border-green-300 text-green-700 rounded-lg shadow-lg max-w-md">
                        <div className="flex items-center">
                            <svg className="w-5 h-5 mr-2 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            {successMessage}
                        </div>
                    </div>
                </div>
            )}

            <FloatingToast variant="error" message={errorMessage} visible={!!errorMessage} />
        </main>
    );
};
